import { Building2 } from "lucide-react";
import { commercialWashingHeroOpt as commercialWashingHero } from "../assets/opt";
import type { CityServiceContent } from "./city-services";

type CommercialCity = {
  city: string;
  slug: string;
  local: string;
  properties: string;
  hoods: string[];
};

const COMMERCIAL_CITIES: CommercialCity[] = [
  {
    city: "Albany",
    slug: "albany",
    local:
      "Downtown Albany storefronts, Central Ave retail, the Washington Ave office corridor, and apartment portfolios through Pine Hills all deal with the same three things: sidewalk gum and grease, black streaking on the building face, and dumpster pads that smell by July.",
    properties:
      "office parks, downtown storefronts, restaurants, apartment complexes, medical offices, and municipal buildings",
    hoods: ["Downtown", "Central Ave", "Washington Ave Ext", "Pine Hills", "Warehouse District", "Delaware Ave", "Corporate Woods", "Menands border"],
  },
  {
    city: "Schenectady",
    slug: "schenectady",
    local:
      "State Street retail, Mohawk Harbor, the Stockade's historic masonry, and the industrial corridor out toward Rotterdam each need a different approach — historic brick gets low pressure, loading docks and pads get hot water and degreaser.",
    properties:
      "retail plazas, restaurants, historic masonry buildings, warehouses, and multi-family portfolios",
    hoods: ["State Street", "Mohawk Harbor", "Stockade District", "Union Street", "Rotterdam corridor", "Glenville", "Scotia", "Erie Blvd"],
  },
  {
    city: "Troy",
    slug: "troy",
    local:
      "Troy's downtown is largely historic brick and brownstone with storefronts at street level, which means the building wash has to be gentle while the sidewalk in front of it needs real pressure and hot water to lift gum and grease.",
    properties:
      "downtown storefronts, restaurants, historic commercial blocks, campus buildings, and apartment portfolios",
    hoods: ["Downtown Troy", "River Street", "Congress Street", "Lansingburgh", "Hoosick Street corridor", "South Troy", "RPI area", "Watervliet border"],
  },
  {
    city: "Saratoga Springs",
    slug: "saratoga-springs",
    local:
      "Broadway storefronts, hotels, and restaurant patios in Saratoga live and die on curb appeal through track season, so most of our work here is scheduled overnight or pre-dawn and timed around the summer calendar.",
    properties:
      "hotels, Broadway retail, restaurants and patios, event venues, and property-managed multi-family buildings",
    hoods: ["Broadway", "Downtown", "Congress Park area", "Church Street", "Excelsior Ave", "Geyser Crest", "Saratoga Lake area", "Wilton border"],
  },
];

export const COMMERCIAL_CITY_SLUGS = COMMERCIAL_CITIES.map(
  (c) => `commercial-pressure-washing-${c.slug}-ny`,
);

export const CITY_SERVICES_COMMERCIAL: CityServiceContent[] = COMMERCIAL_CITIES.map((f) => ({
  slug: `commercial-pressure-washing-${f.slug}-ny`,
  service: "Commercial Pressure Washing",
  city: f.city,
  region: "Capital Region, NY",
  icon: Building2,
  heroImage: commercialWashingHero.url,
  hero: {
    eyebrow: `${f.city}, NY · Insured · COI On File`,
    headline: `Commercial Pressure Washing in ${f.city}, NY`,
    subhead: `Storefronts, sidewalks, dumpster pads, awnings, and building exteriors cleaned on your schedule — early mornings, overnights, or weekends so ${f.city} customers never see the hoses.`,
  },
  intro: [
    `Commercial work is judged on two things: how the property looks Monday morning, and whether the crew got in and out without disrupting business. We schedule ${f.city} accounts around your hours, carry a COI your property manager can put on file, and quote everything as one firm number.`,
    f.local,
    `We handle ${f.properties} throughout ${f.city} — one-time cleanups, seasonal programs, and recurring monthly service.`,
  ],
  localContext: {
    heading: `What ${f.city} Properties Need Most`,
    body: `Every commercial property here fights the same short list: foot-traffic grime on the walk, grease and odor at the back of the house, algae on the shaded side of the building, and salt haze from November through April.`,
    bullets: [
      { title: "Sidewalks & Entryways", desc: "Gum, spills, and foot-traffic grime lifted with a surface cleaner and hot water so the concrete comes back even, not striped." },
      { title: "Dumpster Pads & Drive-Thrus", desc: "Degreased, washed, and deodorized on a recurring schedule before the smell becomes a complaint." },
      { title: "Building Exteriors", desc: "Soft washing removes mildew and streaking from EIFS, brick, metal, and painted surfaces without driving water into seams." },
      { title: "Awnings & Signage", desc: "Fabric and metal awnings washed by hand or low pressure so lettering and stitching stay intact." },
    ],
  },
  neighborhoods: f.hoods,
  benefits: [
    { title: "Off-Hours Scheduling", desc: "Overnight, pre-dawn, or weekend service so your customers and tenants are never in the way." },
    { title: "COI On File", desc: "Fully insured with a certificate of insurance your property manager or corporate office can keep on record." },
    { title: "Recurring Programs", desc: "Monthly, quarterly, or seasonal schedules with the same crew and the same price every visit." },
    { title: "Right Method Per Surface", desc: "Hot water and pressure on flatwork, soft wash on the building — never one setting for the whole property." },
    { title: "Multi-Site Pricing", desc: "One point of contact and one invoice for portfolios with several buildings around the Capital Region." },
    { title: "Net-30 Available", desc: "Approved commercial accounts can be billed net-30 instead of paying on completion." },
  ],
  process: [
    { title: "Site Walk", desc: `We walk the ${f.city} property with you, scope each surface, and note access, water, and drainage.` },
    { title: "Written Bid", desc: "One firm price per visit, with recurring options priced alongside the one-time number." },
    { title: "Schedule Around You", desc: "Work is set for the hours that don't cost you business — usually before open or after close." },
    { title: "Protect & Contain", desc: "Entries, storefront glass, landscaping, and drains protected before anything is applied." },
    { title: "Clean & Rinse", desc: "Flatwork surface-cleaned, building faces soft washed, pads degreased and deodorized, everything rinsed." },
    { title: "Photo Report", desc: "Before-and-after photos sent after each visit so off-site managers can see the work without driving out." },
  ],
  faqs: [
    { q: `How much does commercial pressure washing cost in ${f.city}?`, a: `It's scoped per property. Small storefront sidewalks and entries often start around $250–$450 per visit, while full building exteriors, plazas, and multi-site portfolios are quoted after a site walk. Recurring schedules are priced lower per visit than one-offs.` },
    { q: "Can you work outside business hours?", a: "Yes, and most of our commercial work is done that way — overnight, before open, or on weekends so there's no disruption to customers or tenants." },
    { q: "Are you insured, and can we get a COI?", a: "Yes. We're fully insured and will send a certificate of insurance directly to your property manager or corporate office before the first visit." },
    { q: "Do you offer recurring service?", a: "Yes. Monthly, quarterly, and seasonal programs are the most common for restaurants, retail plazas, and property-managed buildings." },
    { q: "Can you clean dumpster pads and drive-thrus?", a: "Yes — degreasing, hot-water washing, and deodorizing. These are the surfaces most likely to generate a complaint, so most accounts put them on a recurring schedule." },
    { q: "Do you handle multiple locations?", a: "Yes. Multi-site portfolios across the Capital Region get one point of contact, consistent pricing, and a single monthly invoice." },
  ],
  meta: {
    title: `Commercial Pressure Washing in ${f.city}, NY | COI On File`,
    description: `Commercial pressure washing in ${f.city}, NY — storefronts, sidewalks, dumpster pads, awnings, and building exteriors. Off-hours scheduling, insured, net-30.`,
  },
  relatedServices: [
    { name: "Commercial Building Washing", to: "/$service", slug: "commercial-building-washing", blurb: "Soft washing for EIFS, brick, metal, and painted building faces." },
    { name: "Dumpster Pad Cleaning", to: "/$service", slug: "commercial-dumpster-pad-cleaning", blurb: "Degreased, washed, and deodorized on a recurring schedule." },
    { name: "Fleet Washing", to: "/$service", slug: "commercial-fleet-washing", blurb: "Trucks, vans, and trailers washed on site overnight." },
  ],
  relatedCityLinks: [
    { name: `Pressure Washing in ${f.city}, NY`, to: `/pressure-washing-${f.slug}-ny`, blurb: "Everything we clean around properties in town." },
    { name: "Commercial Pressure Washing", to: "/commercial-pressure-washing", blurb: "All commercial services and industries we serve." },
  ],
}));
